import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@h2/ui';
import { Button, Input, Badge, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@h2/ui';
import { Search, Plus, Users, UserCheck, UserX, Clock, Mail, Phone, MessageSquare, Grid3X3, List, Star, MoreHorizontal, Edit, UserMinus, MessageCircle, Trash2, HelpCircle, X } from 'lucide-react';
import { useWorkerStore, Worker } from '../stores/useWorkerStore';
import { WorkerFormModal } from '../components/worker/WorkerFormModal';
import { DeleteConfirmModal } from '../components/worker/DeleteConfirmModal';
import { AdvancedSearchInput, SearchHelpItem } from '../components/search/AdvancedSearchInput';
import { useSearchParams } from '../hooks/useSearchParams';

const WorkerManagementPage: React.FC = () => {
  const {
    workers,
    isLoading: workersLoading,
    searchQuery: workerSearchQuery,
    statusFilter: workerStatusFilter,
    loadWorkers,
    deleteWorker,
    setSearchQuery: setWorkerSearchQuery,
    setStatusFilter: setWorkerStatusFilter,
    getFilteredWorkers
  } = useWorkerStore();


  const [showWorkerForm, setShowWorkerForm] = useState(false);
  const [editingWorker, setEditingWorker] = useState<Worker | null>(null);
  const [deletingWorker, setDeletingWorker] = useState<Worker | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // URL 검색 파라미터 관리
  const { 
    searchState, 
    updateSearchState, 
    clearSearchState, 
    shareSearchState,
    hasActiveSearch 
  } = useSearchParams({
    workerQuery: '',
    workerStatus: 'all',
    viewMode: 'grid'
  });

  useEffect(() => {
    loadWorkers();
  }, [loadWorkers]);

  // URL 파라미터와 로컬 상태 동기화
  useEffect(() => {
    if (searchState.workerQuery !== undefined) {
      setWorkerSearchQuery(searchState.workerQuery);
    }
    if (searchState.workerStatus !== undefined) {
      setWorkerStatusFilter(searchState.workerStatus as 'all' | 'active' | 'inactive');
    }
    if (searchState.viewMode !== undefined) {
      setViewMode(searchState.viewMode as 'grid' | 'list');
    }
  }, [searchState]);

  const filteredWorkers = getFilteredWorkers();

  const handleEditWorker = (worker: Worker) => {
    setEditingWorker(worker);
    setShowWorkerForm(true);
  };

  const handleEditGroup = (group: WorkerGroup) => {
    setEditingGroup(group);
    setShowGroupForm(true);
  };

  const handleDeleteWorker = async () => {
    if (deletingWorker) {
      await deleteWorker(deletingWorker.id);
      setDeletingWorker(null);
    }
  };

  const handleDeleteGroup = async () => {
    if (deletingGroup) {
      await deleteGroup(deletingGroup.id);
      setDeletingGroup(null);
    }
  };

  const getWorkerById = (id: string) => workers.find(w => w.id === id);

  // 검색 도움말 항목
  const workerSearchHelp: SearchHelpItem[] = [
    {
      key: '언어등급',
      description: '특정 언어의 등급으로 검색',
      example: 'EN:A'
    },
    {
      key: '이름',
      description: '작업자 이름으로 검색',
      example: '이름:김민수'
    },
    {
      key: '전문분야',
      description: '전문분야 태그로 검색 (부분일치)',
      example: '전문분야:기술'
    },
    {
      key: '경력',
      description: '경력 정보로 검색 (부분일치)',
      example: '경력:"5년 이상"'
    },
    {
      key: '스킬',
      description: '보유 스킬로 검색',
      example: '스킬:번역'
    },
    {
      key: '상태',
      description: '작업자 상태로 검색',
      example: '상태:active'
    }
  ];

  const formatLastActive = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return '방금 전';
    if (diffMins < 60) return `${diffMins}분 전`;
    if (diffHours < 24) return `${diffHours}시간 전`;
    if (diffDays < 7) return `${diffDays}일 전`;
    return date.toLocaleDateString('ko-KR');
  };

  const WorkerCard: React.FC<{ worker: Worker }> = ({ worker }) => (
    <Card className="h-full group relative">
      {/* 드롭다운 메뉴 */}
      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => handleEditWorker(worker)}>
              <Edit className="mr-2 h-4 w-4" />
              수정
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => console.log('비활성화:', worker.id)}>
              <UserMinus className="mr-2 h-4 w-4" />
              비활성화
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => console.log('그룹 추가:', worker.id)}>
              <Users className="mr-2 h-4 w-4" />
              그룹 추가
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => console.log('채팅:', worker.id)}>
              <MessageCircle className="mr-2 h-4 w-4" />
              채팅
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={() => setDeletingWorker(worker)}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              삭제
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="relative">
              <div className="text-2xl">{worker.avatar || '👤'}</div>
              {/* 접속 상태 표시 */}
              <div className={`absolute -bottom-1 -right-1 w-3 h-3 rounded-full border-2 border-white ${
                worker.isOnline ? 'bg-green-500' : 'bg-gray-400'
              }`} />
            </div>
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                {worker.name}
                {worker.isOnline && <span className="text-green-500 text-sm">●</span>}
              </CardTitle>
              <CardDescription className="flex items-center gap-2">
                <Mail className="h-3 w-3" />
                {worker.email}
              </CardDescription>
            </div>
          </div>
          <Badge variant={worker.status === 'active' ? 'default' : 'secondary'}>
            {worker.status === 'active' ? '활성' : '비활성'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* 단가 정보 */}
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="flex justify-between">
            <span className="text-muted-foreground">기본 단가:</span>
            <span className="font-medium">{worker.hourlyRate.toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">최종 단가:</span>
            <span className="font-medium text-primary">{worker.finalRate?.toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">평균 단가:</span>
            <span className="font-medium">{worker.averageRate?.toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">작업 수:</span>
            <span className="font-medium">{worker.completedTasksCount}개</span>
          </div>
        </div>

        {/* 언어 등급 */}
        {worker.languageLevels && worker.languageLevels.length > 0 && (
          <div className="space-y-2">
            <span className="text-sm text-muted-foreground">언어 등급</span>
            <div className="flex flex-wrap gap-1">
              {worker.languageLevels.map((lang) => (
                <Badge key={lang.language} variant="outline" className="text-xs">
                  {lang.language}: {lang.level}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* 태그 */}
        {worker.tags && worker.tags.length > 0 && (
          <div className="space-y-2">
            <span className="text-sm text-muted-foreground">태그</span>
            <div className="flex flex-wrap gap-1">
              {worker.tags.slice(0, 3).map((tag) => (
                <Badge key={tag.key} variant="secondary" className="text-xs">
                  {tag.key}: {tag.value}
                </Badge>
              ))}
              {worker.tags.length > 3 && (
                <Badge variant="secondary" className="text-xs">
                  +{worker.tags.length - 3}개
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* 메모 */}
        {worker.memo && (
          <div className="space-y-1">
            <div className="flex items-center gap-1">
              <MessageSquare className="h-3 w-3 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">메모</span>
            </div>
            <p className="text-xs text-muted-foreground bg-muted p-2 rounded">
              {worker.memo.length > 60 ? `${worker.memo.substring(0, 60)}...` : worker.memo}
            </p>
          </div>
        )}

        {/* 최근 활동 */}
        {worker.lastActiveAt && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            최근 활동: {formatLastActive(worker.lastActiveAt)}
          </div>
        )}

      </CardContent>
    </Card>
  );

  const WorkerListItem: React.FC<{ worker: Worker }> = ({ worker }) => (
    <div className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 group relative">
      {/* 드롭다운 메뉴 */}
      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => handleEditWorker(worker)}>
              <Edit className="mr-2 h-4 w-4" />
              수정
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => console.log('비활성화:', worker.id)}>
              <UserMinus className="mr-2 h-4 w-4" />
              비활성화
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => console.log('그룹 추가:', worker.id)}>
              <Users className="mr-2 h-4 w-4" />
              그룹 추가
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => console.log('채팅:', worker.id)}>
              <MessageCircle className="mr-2 h-4 w-4" />
              채팅
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={() => setDeletingWorker(worker)}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              삭제
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="flex items-center space-x-4 flex-1">
        {/* 아바타와 기본 정보 */}
        <div className="relative">
          <div className="text-3xl">{worker.avatar || '👤'}</div>
          <div className={`absolute -bottom-1 -right-1 w-3 h-3 rounded-full border-2 border-white ${
            worker.isOnline ? 'bg-green-500' : 'bg-gray-400'
          }`} />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <h3 className="font-semibold text-lg truncate">{worker.name}</h3>
            {worker.isOnline && <span className="text-green-500 text-sm">●</span>}
            <Badge variant={worker.status === 'active' ? 'default' : 'secondary'} className="text-xs">
              {worker.status === 'active' ? '활성' : '비활성'}
            </Badge>
          </div>
          
          <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
            <Mail className="h-3 w-3" />
            <span className="truncate">{worker.email}</span>
            {worker.phone && (
              <>
                <Phone className="h-3 w-3 ml-2" />
                <span>{worker.phone}</span>
              </>
            )}
          </div>
          
          {/* 언어 등급 */}
          {worker.languageLevels && worker.languageLevels.length > 0 && (
            <div className="flex gap-1 mt-2">
              {worker.languageLevels.slice(0, 4).map((lang) => (
                <Badge key={lang.language} variant="outline" className="text-xs">
                  {lang.language}: {lang.level}
                </Badge>
              ))}
              {worker.languageLevels.length > 4 && (
                <Badge variant="outline" className="text-xs">
                  +{worker.languageLevels.length - 4}
                </Badge>
              )}
            </div>
          )}
        </div>
        
        {/* 단가 정보 */}
        <div className="text-right min-w-0">
          <div className="text-sm text-muted-foreground">최종 단가</div>
          <div className="font-semibold text-primary">
            {worker.finalRate?.toLocaleString()} {worker.currency}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            작업 수: {worker.completedTasksCount}개
          </div>
        </div>
        
        {/* 최근 활동 */}
        <div className="text-right min-w-0">
          <div className="text-xs text-muted-foreground">최근 활동</div>
          <div className="text-sm">
            {worker.lastActiveAt ? formatLastActive(worker.lastActiveAt) : '없음'}
          </div>
          {worker.memo && (
            <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
              <MessageSquare className="h-3 w-3" />
              <span className="truncate max-w-20">
                {worker.memo.length > 20 ? `${worker.memo.substring(0, 20)}...` : worker.memo}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const GroupCard: React.FC<{ group: WorkerGroup }> = ({ group }) => (
    <Card className="h-full">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="text-2xl">👥</div>
            <div>
              <CardTitle className="text-lg">{group.name}</CardTitle>
              <CardDescription>{group.description}</CardDescription>
            </div>
          </div>
          <Badge variant={group.status === 'active' ? 'default' : 'secondary'}>
            {group.status === 'active' ? '활성' : '비활성'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">멤버 수</span>
          <span className="font-medium">{group.members.length}명</span>
        </div>
        
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">완료 프로젝트</span>
          <span className="font-medium">{group.totalProjects}개</span>
        </div>
        
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">평균 평점</span>
          <div className="flex items-center gap-1">
            <Star className="h-3 w-3 text-yellow-400" />
            <span className="font-medium">{group.avgRating}</span>
          </div>
        </div>

        {group.teamLead && (
          <div className="text-sm">
            <span className="text-muted-foreground">팀 리더: </span>
            <span className="font-medium">
              {getWorkerById(group.teamLead)?.name || '알 수 없음'}
            </span>
          </div>
        )}

        <div className="space-y-2">
          <span className="text-sm text-muted-foreground">전문 분야</span>
          <div className="flex flex-wrap gap-1">
            {group.specialties.map((specialty) => (
              <Badge key={specialty} variant="outline" className="text-xs">
                {specialty}
              </Badge>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <span className="text-sm text-muted-foreground">멤버</span>
          <div className="flex flex-wrap gap-1">
            {group.members.slice(0, 3).map((memberId) => {
              const member = getWorkerById(memberId);
              return member ? (
                <Badge key={memberId} variant="secondary" className="text-xs">
                  {member.name}
                </Badge>
              ) : null;
            })}
            {group.members.length > 3 && (
              <Badge variant="secondary" className="text-xs">
                +{group.members.length - 3}명
              </Badge>
            )}
          </div>
        </div>

        <div className="flex gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleEditGroup(group)}
            className="flex-1"
          >
            수정
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDeletingGroup(group)}
            className="flex-1"
          >
            삭제
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">작업자 관리</h1>
          <p className="text-muted-foreground">
            작업자를 관리합니다
          </p>
        </div>
      </div>

      {/* 작업자 관리 */}
      <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <AdvancedSearchInput
                value={workerSearchQuery}
                onChange={(query) => {
                  setWorkerSearchQuery(query);
                  updateSearchState({ workerQuery: query });
                }}
                placeholder="EN:A 번역 전문분야:'기술 번역' - 복합 검색"
                helpItems={workerSearchHelp}
                className="w-96"
                onShare={(url) => {
                  navigator.clipboard.writeText(shareSearchState({ 
                    ...searchState, 
                    workerQuery: workerSearchQuery,
                    workerStatus: workerStatusFilter,
                    viewMode
                  }));
                }}
              />
              <div className="flex space-x-2">
                <Button
                  variant={workerStatusFilter === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setWorkerStatusFilter('all');
                    updateSearchState({ workerStatus: 'all' });
                  }}
                >
                  전체
                </Button>
                <Button
                  variant={workerStatusFilter === 'active' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setWorkerStatusFilter('active');
                    updateSearchState({ workerStatus: 'active' });
                  }}
                >
                  <UserCheck className="h-4 w-4 mr-1" />
                  활성
                </Button>
                <Button
                  variant={workerStatusFilter === 'inactive' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setWorkerStatusFilter('inactive');
                    updateSearchState({ workerStatus: 'inactive' });
                  }}
                >
                  <UserX className="h-4 w-4 mr-1" />
                  비활성
                </Button>
              </div>
              
              {/* 뷰 모드 토글 */}
              <div className="flex space-x-1 border rounded-md">
                <Button
                  variant={viewMode === 'grid' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => {
                    setViewMode('grid');
                    updateSearchState({ viewMode: 'grid' });
                  }}
                  className="rounded-r-none"
                >
                  <Grid3X3 className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => {
                    setViewMode('list');
                    updateSearchState({ viewMode: 'list' });
                  }}
                  className="rounded-l-none"
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="flex space-x-2">
              {hasActiveSearch && (
                <Button variant="outline" onClick={() => {
                  setWorkerSearchQuery('');
                  setWorkerStatusFilter('all');
                  setViewMode('grid');
                  clearSearchState();
                }}>
                  <X className="h-4 w-4 mr-2" />
                  검색 초기화
                </Button>
              )}
              <Button onClick={() => setShowWorkerForm(true)}>
                <Plus className="h-4 w-4 mr-2" />
                새 작업자
              </Button>
            </div>
          </div>

          {workersLoading ? (
            <div className="text-center py-8 text-muted-foreground">
              작업자 목록을 불러오는 중...
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredWorkers.map((worker) => (
                <WorkerCard key={worker.id} worker={worker} />
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredWorkers.map((worker) => (
                <WorkerListItem key={worker.id} worker={worker} />
              ))}
            </div>
          )}

          {!workersLoading && filteredWorkers.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              {workerSearchQuery || workerStatusFilter !== 'all' 
                ? '검색 조건에 맞는 작업자가 없습니다.' 
                : '등록된 작업자가 없습니다.'}
            </div>
          )}
      </div>

      {/* 모달들 */}
      {showWorkerForm && (
        <WorkerFormModal
          worker={editingWorker}
          onClose={() => {
            setShowWorkerForm(false);
            setEditingWorker(null);
          }}
        />
      )}

      {deletingWorker && (
        <DeleteConfirmModal
          title="작업자 삭제"
          message={`'${deletingWorker.name}' 작업자를 삭제하시겠습니까?`}
          onConfirm={handleDeleteWorker}
          onCancel={() => setDeletingWorker(null)}
        />
      )}
    </div>
  );
};

export default WorkerManagementPage;
